import React, { useMemo, memo } from 'react';
import RowsPerPageSelect from 'components/admin/RowsPerPageSelect';

interface PaginationProps {
  currentPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  // Optional "Rows per page" control. When onPageSizeChange is provided a
  // selector is rendered; otherwise the component behaves exactly as before.
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  // Set to false when totalItems is an estimate (e.g. peek-ahead paging) and
  // the "Total N records" label would be misleading.
  showTotal?: boolean;
}

const DEFAULT_ROWS_PER_PAGE = 10;

const Pagination: React.FC<PaginationProps> = memo(({
  currentPage,
  totalItems,
  onPageChange,
  pageSize = DEFAULT_ROWS_PER_PAGE,
  onPageSizeChange,
  pageSizeOptions = [10, 50, 100],
  showTotal = true,
}) => {

  const totalPages = Math.ceil(totalItems / pageSize);

  const generatePageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  const paginatedPageNumbers = useMemo(() => generatePageNumbers(), [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
      <div className="flex flex-wrap items-center gap-5">
        {showTotal && (
          <p className="text-lightgray-200 text-sm">
            Total {totalItems} {totalItems === 1 ? "record" : "records"}
          </p>
        )}
        {onPageSizeChange && (
          <>
            <p className="text-lightgray-200 text-sm">Rows per page</p>
            <RowsPerPageSelect
              value={pageSize}
              onChange={onPageSizeChange}
              options={pageSizeOptions}
            />
          </>
        )}
      </div>

      <div>
        <ul className="flex flex-wrap items-center justify-center gap-3 text-sm">
          <li>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                currentPage === 1
                  ? "text-gray-300 border-gray-300"
                  : "text-primaryblue-50 border-primaryblue-50"
              }`}
              aria-label="Previous page"
            >
              <span className="sr-only">Previous</span>
              <i className="st-arrow-right text-xl font-semibold rotate-180"></i>
            </button>
          </li>
          {paginatedPageNumbers.map((page) => (
            <li key={page}>
              <button
                onClick={() => handlePageChange(page)}
                className={`flex items-center justify-center w-7 h-7 rounded-md border border-primaryblue-50 ${
                  currentPage === page ? 'text-white bg-primaryblue-50' : 'text-primaryblue-50'
                }`}
                aria-label={`Page ${page}`}
              >
                {page}
              </button>
            </li>
          ))}
          <li>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`flex items-center justify-center w-7 h-7 leading-tight border rounded-md ${
                currentPage === totalPages
                  ? "text-gray-300 border-gray-300"
                  : "text-primaryblue-50 border-primaryblue-50"
              }`}
              aria-label="Next page"
            >
              <span className="sr-only">Next</span>
              <i className="st-arrow-right text-xl font-semibold"></i>
            </button>
          </li>
        </ul>
      </div>
    </div>
  );
});

Pagination.displayName = "Pagination";

export default Pagination;